import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DeleteHook from "../../../Hooks/DeleteHook";
import { singleImageUpload } from "../../../Hooks/ImageUpload";
import PostHooks from "../../../Hooks/PostHooks";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import { useQuery } from "../../../Hooks/useApi";

const SuperAdminDoctorReview = () => {
  const [imageUrl, setImageUrl] = useState("");
  const [refetch, setRefetch] = useState(false);
  const { data: reviewsResponse } = useQuery("/doctorReview/getdoctorReview");
  const doctors = reviewsResponse?.data || [];
  const [formData, setFormData] = useState({
    doctorName: "",
    doctorReview: "",
    specialist: "",
  });

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (formData?._id) {
        await UpdateHooks(
          `https://servercareseebd.careseebd.com/api/v1/doctorReview/updateDoctorReview/${formData?._id}`,
          { ...formData, doctorImg: imageUrl }
        );
        toast.success("Doctor review updated successfully!");
        setFormData({ doctorName: "", doctorReview: "", specialist: "" });
        setImageUrl("");
        setRefetch(!refetch);
      } else {
        await PostHooks(
          `https://servercareseebd.careseebd.com/api/v1/doctorReview/adddoctorReview`,
          { ...formData, doctorImg: imageUrl },
          "Doctor Review posted"
        );
        setFormData({
          doctorName: "",
          doctorReview: "",
          specialist: "",
        });
        setImageUrl("");
        setTimeout(() => {
          setRefetch(!refetch);
        }, 1000);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleChangeUploadImage = async (event) => {
    const image = event.target.files[0];
    const formData = new FormData();
    formData.append("image", image);
    singleImageUpload(formData, setImageUrl);
  };

  return (
    <div className="max-w-6xl mx-auto select-none space-y-6">
      {/* Header bar */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:chat-square-like-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Doctor Testimonials</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Manage doctor reviews, designations, and profile photos</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form panel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4 h-fit">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">
            {formData?._id ? "Edit Review" : "Add Doctor Review"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Doctor Photo</label>
              <div className="space-y-2">
                <input
                  type="file"
                  onChange={handleChangeUploadImage}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs file:mr-3 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-[10px] file:font-extrabold file:uppercase file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
                  accept="image/*"
                />
                {(imageUrl || formData?.doctorImg) && (
                  <div className="w-16 h-16 rounded-xl border border-slate-100 overflow-hidden bg-slate-50 p-1 flex items-center justify-center">
                    <img className="max-w-full max-h-full object-contain rounded-lg" src={imageUrl || formData?.doctorImg} alt="preview" />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Doctor Name</label>
              <input
                type="text"
                name="doctorName"
                onChange={handleChange}
                value={formData.doctorName}
                required
                placeholder="Dr. Alice Smith"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Specialist Designation</label>
              <input
                type="text"
                name="specialist"
                onChange={handleChange}
                value={formData.specialist}
                required
                placeholder="e.g. Cardiologist"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-semibold"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide">Review Quote</label>
              <textarea
                name="doctorReview"
                onChange={handleChange}
                value={formData.doctorReview}
                required
                rows="4"
                placeholder="Write the feedback review here..."
                className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-xs font-medium"
              />
            </div>

            <div className="flex gap-2">
              {formData?._id && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ doctorName: "", doctorReview: "", specialist: "" });
                    setImageUrl("");
                  }}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-500 font-extrabold text-[11px] transition-all hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="flex-1 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-[11px] rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
              >
                {formData?._id ? "Update Review" : "Add Review"}
              </button>
            </div>
          </form>
        </div>

        {/* List panel */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-wider">Active Doctor Reviews</h2>
          <div className="overflow-x-auto border border-slate-100 rounded-xl">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/75 border-b border-slate-100">
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Doctor</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Specialist</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Review</th>
                  <th className="px-6 py-4 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {doctors.length > 0 ? (
                  doctors.map((doctor) => (
                    <tr key={doctor?._id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 text-xs font-bold text-slate-800">
                        <div className="flex items-center gap-3">
                          {doctor?.doctorImg ? (
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-50 border border-slate-100 p-0.5 flex items-center justify-center">
                              <img className="max-w-full max-h-full object-cover rounded-full" src={doctor?.doctorImg} alt={doctor?.doctorName} />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-700 font-extrabold flex items-center justify-center uppercase">
                              {(doctor?.doctorName || "D").substring(0, 1)}
                            </div>
                          )}
                          <span>{doctor?.doctorName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs font-semibold text-indigo-600">
                        {doctor?.specialist}
                      </td>
                      <td className="px-6 py-4 text-xs font-medium text-slate-500 max-w-xs truncate" title={doctor?.doctorReview}>
                        {doctor?.doctorReview}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex justify-end items-center gap-2">
                          <button
                            onClick={async () => {
                              setFormData(doctor);
                              setImageUrl(doctor?.doctorImg || "");
                              scrollToTop();
                            }}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-150 rounded-lg transition-all cursor-pointer"
                            title="Edit Review"
                          >
                            <Icon icon="basil:edit-outline" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() =>
                              DeleteHook({
                                setRefetch,
                                refetch,
                                url: `https://servercareseebd.careseebd.com/api/v1/doctorReview/deleteDoctorReview/${doctor?._id}`,
                              })
                            }
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 hover:border-rose-150 rounded-lg transition-all cursor-pointer"
                            title="Delete Review"
                          >
                            <Icon icon="material-symbols:delete-outline" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-10 text-xs text-slate-400">
                      No doctor reviews added.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDoctorReview;
